import { Box, Panel } from '@hover/blueprint';
import { iconChevronLeft } from '@hover/icons';
import React, { useEffect } from 'react';
import { animated, useSpring } from 'react-spring';

import { animationConfig } from 'src/modules/designStudio/helpers/animationConfig';
import { NavButton } from 'src/shared/components/NavButton';
import {
  TranslationNamespace,
  useReactNativeWebView,
  useTranslate,
} from 'src/shared/hooks';
import { backButtonPressed } from 'src/shared/telemetry';

import { BackgroundLogo } from '../../BackgroundLogo';
import { FastLoader } from '../../FastLoader';

import { AnimatedHeader } from './AnimatedHeader';
import { AnimatedText } from './AnimatedText';

const AnimatedBody = ({
  finishLoadingAnimation,
  simple,
  children,
  hideBackButton: propsHideBackButton,
}: {
  finishLoadingAnimation?: () => void;
  simple: boolean;
  children?: React.ReactNode;
  hideBackButton?: boolean;
}) => {
  const [isWebView, sendReactNativeMessage] = useReactNativeWebView();
  useEffect(() => {
    if (!finishLoadingAnimation) return () => {};
    // timeout is calculated by adding up the duration of each step
    const timer = setTimeout(finishLoadingAnimation, 4950);

    return () => clearTimeout(timer);
  }, [finishLoadingAnimation]);

  const loaderContainerStyle = useSpring(animationConfig.loaderAnimation);
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);

  // Allows overriding showing or hiding the back button.
  // Otherwise, by default it will only in a webview.
  const hideBackButton = propsHideBackButton ?? !isWebView;

  // Simple allows to ignore children.
  // If children are passed, it will render them otherwise it will render
  // the default loading text.
  // TODO: Possibly refactor call sites to use only children prop and remove `simple` prop.
  const renderedChildren = simple
    ? null
    : children || (
        <Box justifyContent="center" width="100%">
          <AnimatedText
            animationStep={1}
            color="neutral000"
            key="experiment"
            size={400}
          >
            {t('loading.experimentWithColors')}
          </AnimatedText>
          <AnimatedText
            animationStep={2}
            color="neutral000"
            key="material"
            size={400}
          >
            {t('loading.tryNewMaterials')}
          </AnimatedText>
          <AnimatedText
            animationStep={3}
            color="neutral000"
            key="share"
            size={400}
          >
            {t('loading.shareYourDesign')}
          </AnimatedText>
          <AnimatedHeader
            animationStep={4}
            color="neutral000"
            key="creative"
            size={500}
          >
            {t('loading.letsGetCreative')}
          </AnimatedHeader>
        </Box>
      );

  return (
    <Panel
      alignItems="center"
      backgroundColor="primaryBase"
      borderRadius="0"
      height="100vh"
      justifyContent="center"
      overflow="hidden"
      position="absolute"
      testId="loading-screen"
      top="0"
      width="100vw"
    >
      <BackgroundLogo />
      {!hideBackButton && (
        <Box left={300} position="absolute" top={700}>
          <NavButton
            icon={iconChevronLeft}
            onClick={() => sendReactNativeMessage('handleNativeBack')}
            testId="back-button"
            track={backButtonPressed}
          />
        </Box>
      )}
      <animated.div style={loaderContainerStyle}>
        <FastLoader color="neutral.0" />
      </animated.div>
      {renderedChildren}
    </Panel>
  );
};

export function LoadingPage({
  finishLoadingAnimation,
  simple = false,
  children,
  hideBackButton,
}: {
  finishLoadingAnimation?: () => void;
  simple?: boolean;
  children?: React.ReactNode;
  hideBackButton?: boolean;
}) {
  return (
    <AnimatedBody
      finishLoadingAnimation={finishLoadingAnimation}
      hideBackButton={hideBackButton}
      simple={simple}
    >
      {children}
    </AnimatedBody>
  );
}
